import {
  AbstractLinkParseResult,
  AbstractLinkResolver,
  SocialProfile,
  createPlatformAwareParser,
  createPlatformAwareScorer,
  extractHandleFromInputScoring
} from './abstractLinkResolver';
import { ensureHttpsProtocol } from 'v1/helpers/urlHelpers';
import { Maybe } from '@passionware/monads';

export type Platform = keyof typeof socialResolvers;

export const recognizePlatform = (
  input: string,
  preferredPlatform?: Platform
): { platform: Platform; result: AbstractLinkParseResult } | null => {
  const candidates: Array<{
    platform: Platform;
    score: number;
    result: AbstractLinkParseResult;
  }> = [];

  for (const [key, resolver] of Object.entries(socialResolvers)) {
    const parseResult = resolver.parseInput(input);
    if (parseResult) {
      const score = resolver.getMatchingScore(input);
      candidates.push({
        platform: key as Platform,
        score: score,
        result: parseResult
      });
    }
  }

  // If no platform matched, fallback to `other` for non-empty input.
  if (candidates.length === 0) {
    if (!input.trim()) {
      return null;
    }
    return {
      platform: 'other',
      result: {
        handle: input,
        fullUrl: input
      }
    };
  }

  // Sort by score (highest first) and return the best match
  candidates.sort((a, b) => b.score - a.score);
  const bestScore = candidates[0].score;
  const top = candidates.filter(c => c.score === bestScore);

  if (preferredPlatform) {
    const preferred = top.find(c => c.platform === preferredPlatform);
    if (preferred) {
      return {
        platform: preferred.platform,
        result: preferred.result
      };
    }
  }

  return {
    platform: top[0].platform,
    result: top[0].result
  };
};

/**
 * Creates a basic link resolver that uses the platforms logic
 * @param platformBases - The platform's addresses (e.g. 'twitter.com/', 'x.com/'); a handle links to the first
 * @param inputScoring - Optional map of regex → score; first capture group is the handle
 */
export const createBasicSocialResolver = (
  platformBases: ReadonlyArray<string>,
  inputScoring: Maybe<Map<RegExp, number>> = null
): AbstractLinkResolver => {
  return {
    parseInput: createPlatformAwareParser({
      platformBases,
      inputScoring,
      parseFullUrl: (
        inputString,
        normalizedInput,
        platformBase,
        normalizedBase
      ) => {
        const baseIndex = normalizedInput.indexOf(normalizedBase);
        const handle = inputString
          .substring(baseIndex + platformBase.length)
          .split(/\/|\?|#/)[0];
        return {
          handle,
          fullUrl: ensureHttpsProtocol(inputString)
        };
      },
      parseHandle: inputString => {
        const handle = extractHandleFromInputScoring(
          inputString,
          inputScoring ?? new Map<RegExp, number>()
        );
        return {
          handle,
          fullUrl: `https://${platformBases[0]}${handle}`
        };
      }
    }),
    getMatchingScore: createPlatformAwareScorer({
      platformBases,
      inputScoring
    })
  };
};

/**
 * Creates a Discord-specific resolver
 * If user provides only a handle (not a full URL), fullUrl will be https://discord.com
 * @returns An AbstractLinkResolver instance for Discord
 */
export const createDiscordResolver = (): AbstractLinkResolver => {
  const platformBase = 'discord.com/users/';
  const inputScoring = new Map<RegExp, number>([
    [/^dc:(.+)$/i, 0.9], // e.g. dc:myname
    [/^([^#/:]+#\d{4})$/, 0.85], // e.g. user#1234 (legacy; avoids https://…#fragment false positives)
    [/^((?!.*\.\.)(?!.*\.$)(?!^\.)[a-z0-9](?:[a-z0-9._]{0,30}[a-z0-9]))$/, 0.45] // e.g. jankowalski, my.name
  ]);

  return {
    parseInput: createPlatformAwareParser({
      platformBases: [platformBase],
      inputScoring,
      parseFullUrl: (
        inputString,
        normalizedInput,
        platformBase,
        normalizedBase
      ) => {
        // Extract handle from URL
        const baseIndex = normalizedInput.indexOf(normalizedBase);
        const handle = inputString
          .substring(baseIndex + platformBase.length)
          .split(/\/|\?|#/)[0];
        return {
          handle,
          fullUrl: ensureHttpsProtocol(inputString)
        };
      },
      parseHandle: (inputString, normalizedInput) => {
        const handle = extractHandleFromInputScoring(inputString, inputScoring);
        return {
          handle,
          fullUrl: 'https://discord.com'
        };
      }
    }),
    getMatchingScore: createPlatformAwareScorer({
      platformBases: [platformBase],
      inputScoring
    })
  };
};

/**
 * Creates a LinkedIn-specific resolver that handles both personal (/in/) and company (/company/) profiles
 * @returns An AbstractLinkResolver instance for LinkedIn
 */
export const createLinkedInResolver = (): AbstractLinkResolver => {
  const getHandleWithPrefix = (handle: string, isCompany: boolean): string => {
    if (!handle) {
      return '';
    }
    const prefix = isCompany ? 'co/' : 'in/';
    return `${prefix}${handle}`;
  };

  const getProfile = (
    handle: string,
    isCompany: boolean
  ): SocialProfile | null =>
    handle ? { kind: isCompany ? 'company' : 'person', name: handle } : null;

  const inputScoring = new Map<RegExp, number>([
    [/^in:(.+)$/i, 0.9], // e.g. in:jankowalski
    [/^company\/(.*)$/i, 0.85], // e.g. company/acme-corp
    [/^([a-zA-Z0-9-]{3,100})$/, 0.5] // e.g. jan-kowalski (3–100 chars)
  ]);

  return {
    parseInput: createPlatformAwareParser({
      platformBases: ['linkedin.com/'],
      inputScoring,
      parseFullUrl: (inputString, normalizedInput) => {
        // Extract handle from URL if it matches the LinkedIn pattern
        const match = normalizedInput.match(
          /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/(company|in)\/([^/?#]+)/
        );
        if (!match) {
          return null;
        }
        const isCompany = match[1] === 'company';
        // Use original inputString to preserve case for handle
        const originalMatch = inputString.match(
          /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/(company|in)\/([^/?#]+)/i
        );
        const handle = originalMatch ? originalMatch[2] : match[2];
        const basePath = isCompany ? 'company/' : 'in/';
        return {
          handle: getHandleWithPrefix(handle, isCompany),
          fullUrl: ensureHttpsProtocol(`linkedin.com/${basePath}${handle}`),
          profile: getProfile(handle, isCompany)
        };
      },
      parseHandle: (inputString, normalizedInput) => {
        const handle = extractHandleFromInputScoring(inputString, inputScoring);
        const isCompany = normalizedInput.startsWith('company/');
        const basePath = isCompany ? 'company/' : 'in/';
        return {
          handle: getHandleWithPrefix(handle, isCompany),
          fullUrl: `https://linkedin.com/${basePath}${handle}`,
          profile: getProfile(handle, isCompany)
        };
      }
    }),
    getMatchingScore: createPlatformAwareScorer({
      platformBases: ['linkedin.com/'],
      inputScoring
    })
  };
};

/**
 * Pre-configured resolvers for common social media platforms
 */
export const socialResolvers = {
  behance: createBasicSocialResolver(
    ['behance.net/'],
    new Map([[/^([a-zA-Z0-9_]{1,30})$/, 0.5]]) // e.g. my_portfolio, Studio_01
  ),
  discord: createDiscordResolver(),
  dribbble: createBasicSocialResolver(
    ['dribbble.com/'],
    new Map([[/^([a-zA-Z0-9_]{1,20})$/, 0.5]]) // e.g. simplebits, user_name
  ),
  facebook: createBasicSocialResolver(
    ['facebook.com/'],
    new Map([
      [/^fb:(.+)$/i, 0.9], // e.g. fb:PageName
      [/^((?=.{5,50}$)[a-zA-Z0-9](?:[a-zA-Z0-9.]*[a-zA-Z0-9])?)$/, 0.5] // e.g. my.page.name (5–50 chars)
    ])
  ),
  github: createBasicSocialResolver(
    ['github.com/'],
    new Map([
      [/^gh:(.+)$/i, 0.9], // e.g. gh:octocat
      [
        /^((?!-)(?!.*--)(?!.*-$)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,37}[a-zA-Z0-9])?)$/,
        0.5 // e.g. octocat, my-org-name
      ]
    ])
  ),
  instagram: createBasicSocialResolver(
    ['instagram.com/'],
    new Map([
      [/^ig:(.+)$/i, 0.9], // e.g. ig:meta
      [
        /^((?=.*[._])(?!.*\.\.)(?!.*\.$)(?!^\.)[a-z0-9](?:[a-z0-9._]{0,28}[a-z0-9])?)$/,
        0.5 // e.g. iza.gorska (needs . or _ in handle)
      ],
      [
        /^((?!.*\.\.)(?!.*\.$)(?!^\.)[a-z0-9](?:[a-z0-9._]{0,28}[a-z0-9])?)$/,
        0.5 // e.g. jankowalski, user_name
      ]
    ])
  ),
  linkedin: createLinkedInResolver(),
  medium: createBasicSocialResolver(
    ['medium.com/@'],
    new Map([
      [/^@(.*)$/, 0.9], // e.g. @jane
      [/^([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)$/, 0.5] // e.g. my-publication
    ])
  ),
  rednote: createBasicSocialResolver(
    ['xiaohongshu.com/user/profile/'],
    new Map([[/^([a-fA-F0-9]{12,32})$/, 0.5]]) // e.g. 5f1a2b3c4d5e6f708192a1b2c (hex 12–32)
  ),
  soundcloud: createBasicSocialResolver(
    ['soundcloud.com/'],
    new Map([
      [/^((?=.{3,25}$)[a-z0-9](?:[a-z0-9_-]*[a-z0-9])?)$/, 0.5] // e.g. bk_beats, artist-name
    ])
  ),
  spotify: createBasicSocialResolver(
    ['spotify.com/artist/'],
    new Map([[/^([0-9A-Za-z]{22})$/, 0.5]]) // e.g. 06HL4z0CvFAxyc27GXpf02 (22-char id)
  ),
  tiktok: createBasicSocialResolver(
    ['tiktok.com/@'],
    new Map([
      [/^tt:(.+)$/i, 0.9], // e.g. tt:creator
      [/^@(.*)$/, 0.85], // e.g. @tiktok
      [/^((?!.*\.$)(?!.*\.\.)[a-z0-9](?:[a-z0-9._]{0,22}[a-z0-9])?)$/, 0.5] // e.g. creator.name
    ])
  ),
  twitter: createBasicSocialResolver(
    ['twitter.com/', 'x.com/'],
    new Map([[/^([a-zA-Z0-9_]{4,15})$/, 0.5]]) // e.g. jack, jankowalski (4–15 chars)
  ),
  vimeo: createBasicSocialResolver(
    ['vimeo.com/'],
    new Map([[/^([a-zA-Z0-9]{1,64})$/, 0.5]]) // e.g. staff, mychannel99
  ),
  vsco: createBasicSocialResolver(
    ['vsco.co/'],
    new Map([[/^([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)$/, 0.5]]) // e.g. jane, my-name
  ),
  youtube: createBasicSocialResolver(
    [
      'youtube.com/@',
      'youtube.com/channel/',
      'youtube.com/c/',
      'youtube.com/user/'
    ],
    new Map([
      [/^yt:(.+)$/i, 0.9], // e.g. yt:YouTube
      [/^@(.*)$/, 0.85], // e.g. @YouTube
      [/^((?=.{3,30}$)[a-zA-Z0-9](?:[a-zA-Z0-9_.·-]*[a-zA-Z0-9])?)$/, 0.5] // e.g. YouTubeCreators (3–30 chars)
    ])
  ),
  other: createBasicSocialResolver([], new Map()) // no rows; fallback when nothing matches
};
